import angular from "angular";

import { UserEntity } from "../../entity/UserEntity";
import { User } from "../../gateway/User/User";
import { LocalStoreService } from "../../presentation/services/LocalStoreService";
import {
    AuthTokenUsecase,
    AuthenticateUserUsecase,
    GetUsersByIdsUsecase
} from "../../usecases";

export class JWTService {
    LocalStoreService = new LocalStoreService();
    User = new User();
    TOKEN_KEY = "nucont:jwt_token";
    REFRESH_TOKEN_KEY = "nucont:refresh_token";
    USER_KEY = "nucont:user";
    METEOR_USER_ID_KEY = "Meteor.userId";
    user = null;
    token = null;
    refreshToken = null;
    isAuthenticated = false;
    signingIn = false;
    verifyTokenPromise = null;
    verifyTokenResult = null;

    constructor($state) {
        this.$state = $state;
    }

    async signin(email, password) {
        try {
            this.signingIn = true;
            const response = await new AuthenticateUserUsecase().execute({
                email,
                password
            });
            if (response.token) {
                const userData = await this.getUserData({
                    userId: response.user._id,
                    token: response.token
                });
                this._storeSession(
                    response.token,
                    response.refreshToken,
                    userData,
                    !!response.token
                );
                this.signingIn = false;
                this._resetVerifyTokenCache();
                return {
                    response: true,
                    message: "Login realizado com sucesso"
                };
            }
        } catch (error) {
            throw new Error(`${error.message || "Usuário ou senha inválidos"}`);
        }
    }

    async verifyToken() {
        this.signingIn = true;
        let token = this.LocalStoreService.getItem(this.TOKEN_KEY);
        if (!token) {
            this.signout();
            return false;
        }
        const decoded = JSON.parse(atob(token.split(".")?.[1] || ""));
        if (!decoded.exp) {
            this.signout();
            return false;
        }
        const user = this.LocalStoreService.getItem(this.USER_KEY);
        const meteorUserId = this.LocalStoreService.getItem(
            this.METEOR_USER_ID_KEY
        );
        if (meteorUserId) {
            this.signout();
            return false;
        }
        let response = null;
        const expDate = new Date(decoded.exp * 1000);
        if (expDate < new Date()) {
            response = await this.requestRefreshToken();
            if (response) {
                token = response.token;
                this._storeSession(
                    response.token,
                    response.refreshToken,
                    user,
                    !!response.token
                );
                this.signingIn = false;
                return true;
            } else {
                this.signout();
                return false;
            }
        }
        const userData = await this.getUserData({ userId: user._id, token });
        const storedRefreshToken = this.LocalStoreService.getItem(
            this.REFRESH_TOKEN_KEY
        );
        this._storeSession(token, storedRefreshToken, userData, !!token);
        this.signingIn = false;
        return true;
    }

    async verifyTokenOnce() {
        if (this.verifyTokenPromise) return this.verifyTokenPromise;
        this.verifyTokenPromise = (async () => {
            const result = await this.verifyToken();
            this.verifyTokenResult = result;
            return result;
        })().catch((error) => {
            this.verifyTokenPromise = null;
            this.verifyTokenResult = null;
            throw error;
        });
        return this.verifyTokenPromise;
    }

    _resetVerifyTokenCache() {
        this.verifyTokenPromise = null;
        this.verifyTokenResult = null;
    }

    async requestRefreshToken() {
        const refreshToken = this.LocalStoreService.getItem(
            this.REFRESH_TOKEN_KEY
        );
        if (!refreshToken) return null;
        const response = await this.User.refreshToken(refreshToken);
        if (response.token) return response;
        return null;
    }

    getToken() {
        return this.LocalStoreService.getItem(this.TOKEN_KEY);
    }

    getUser() {
        const raw = this.LocalStoreService.getItem(this.USER_KEY);
        if (!raw) return null;
        try {
            return UserEntity.restoreFromCloud(JSON.parse(raw));
        } catch {
            return null;
        }
    }

    signout() {
        this._resetVerifyTokenCache();
        this._storeSession(null, null, null, false);
        this.LocalStoreService.removeItem("user");
        this.LocalStoreService.removeItem(this.METEOR_USER_ID_KEY);
        // window.location.href = "/login";
        this.$state.go("nucont.authentication");
    }

    _storeSession(token, refreshToken, user, isAuthenticated) {
        this.isAuthenticated = isAuthenticated;
        this.token = token;
        this.refreshToken = refreshToken;
        this.user = user;
        this.LocalStoreService.setItem(this.TOKEN_KEY, token);
        this.LocalStoreService.setItem(this.REFRESH_TOKEN_KEY, refreshToken);
        this.LocalStoreService.setItem(this.USER_KEY, user);
    }

    async signByToken(token) {
        try {
            this.signingIn = true;
            const authTokenUsecase = new AuthTokenUsecase();
            const response = await authTokenUsecase.execute({
                authToken: token
            });
            if (!response.token || !response.user?._id) {
                this.signout();
                return false;
            }
            const userData = await this.getUserData({
                userId: response.user._id,
                token: response.token
            });
            this._storeSession(
                response.token,
                response.refreshToken,
                userData,
                !!response.token
            );
            this.signingIn = false;
            this._resetVerifyTokenCache();
            return true;
        } catch (error) {
            throw new Error(
                `${error?.message || "Erro ao autenticar com token"}`
            );
        }
    }

    async getUserData({ userId, token }) {
        if (!userId) return null;
        const getUsersByIdsUsecase = new GetUsersByIdsUsecase();
        const users = await getUsersByIdsUsecase.execute({
            ids: [userId],
            configs: { userId, token }
        });
        if (!users?.length) return null;
        return users[0].props;
    }
}

angular.module("nucont.shared").service("JWTService", ["$state", JWTService]);
